import * as R from 'ramda'

const getTitle = ({
    content,
    locale,
    defaultLocale,
    initialLocale,
}) => {
    const localeOrPath = (o) => (l) => R.or(l, R.path([o], content))
    // alter & return `Other (please specify)`
    const alterOtherValue = (v) => ((typeof v === 'string') ? R.split('[Q |', v)[0] : v)
    const a = R.pipe(
        R.path([locale]),
        localeOrPath(defaultLocale),
        localeOrPath(initialLocale),
        alterOtherValue,
    )(content)

    return a
}

const getQuestionTitle = ({
    content,
    formContent,
    locale,
    defaultLocale,
    initialLocale,
}) => {
    const formTitle = getTitle({
        content: formContent,
        locale,
        defaultLocale,
        initialLocale,
    })

    const title = getTitle({
        content,
        locale,
        defaultLocale,
        initialLocale,
    })

    return formTitle
        ? `${formTitle} - ${title}`
        : title
}

const getOptionMetric = ({
    availableMetrics,
    metricId,
    locale,
    defaultLocale,
    initialLocale,
}) => {
    const content = availableMetrics.get(metricId)

    return getTitle({
        content,
        locale,
        defaultLocale,
        initialLocale,
    })
}

const getOptionTitle = ({
    langs,
    locale,
    defaultLocale,
    initialLocale,
}) => (
    getTitle({
        content: langs,
        locale,
        defaultLocale,
        initialLocale,
    })
)

// An NPS question is stored as a plain 'RD' question flagged with settings.report.isNPS.
// Older NPS questions are identified by metadata.kpiType instead, so both count.
const isNPSQuestion = (question) => (
    R.pathOr(false, ['settings', 'report', 'isNPS'], question)
    || R.path(['metadata', 'kpiType'], question) === 'NPS'
)

// Reports and charts key their NPS rendering off metadata.kpiType. This returns the
// kpiType they should use, promoting an isNPS-flagged RD question to 'NPS'.
const kpiTypeFor = (question) => (
    isNPSQuestion(question) ? 'NPS' : R.path(['metadata', 'kpiType'], question)
)

const isSingleAxis = ({ questionTypeCode }) => (
    ['CO', 'SB', 'CH', 'PC', 'OD', 'RD', 'UV', 'TL'].includes(questionTypeCode)
)

const isDoubleAxis = ({ questionTypeCode }) => (
    ['TB', 'RT'].includes(questionTypeCode)
)

const getParentQuestion = (parentPageQuestionId, survey) => {
    if (!parentPageQuestionId) return null
    const [parentQuestion] = survey.pages.map((p) => R.pathOr([], ['questions'], p).find(({ id }) => id.toUpperCase() === parentPageQuestionId.toUpperCase())).filter((val) => !R.isNil(val))
    return parentQuestion
}

const getParentOptions = (parentId, survey) => {
    const parentQuestion = getParentQuestion(parentId, survey)
    if (!parentQuestion) return []
    return getParentOptions(parentQuestion.parentId, survey)
        .concat(R.pathOr([], ['question', 'optionSet', 'options'], parentQuestion).filter(({ isNonReporting }) => !isNonReporting))
}

const getParentFromQlist = (parentId, questionLibraryLists) => {
    if (questionLibraryLists.size < 1) return null
    const allQListQuestions = Array.from(questionLibraryLists.values()).flat()
    const parentQuestion = allQListQuestions.find((q) => q.questionId.toLowerCase() === parentId)
    return parentQuestion
}

const getQuestionById = async (qid, store) => {
    try {
        const res = await store.http.get(`/api/question/${qid}/7`)
        return res.question
    } catch (error) {
        store.handleError({ body: 'Could not fetch question', level: 'error' })
        console.error(error)
    }
}

// This inserts parent options into child question dynamically. If parent options are missing translations
// in the child's initLocale, it copies them from the parent's initLocale
const insertInheritedOptions = (childQuestion, parentQuestion) => {
    if (!childQuestion || !parentQuestion) return
    const childInitLocale = R.pathOr(null, ['metadata', 'initialLocale'], childQuestion)
    const parentInitLocale = R.pathOr(null, ['question', 'metadata', 'initialLocale'], parentQuestion)

    // copy parent options with child's initLocale (as the inheritedOptions to be inserted)
    const parentOptions = R.pathOr([], ['question', 'optionSet', 'options'], parentQuestion)
        .filter(({ isNonReporting }) => !isNonReporting)
    if (parentOptions.length === 0) return
    const inheritedOptions = parentOptions.map((option) => ({
        ...option,
        langs: { ...option.langs },
    }))
    if (childInitLocale && parentInitLocale) {
        inheritedOptions.forEach((option) => {
            if (!option.langs[childInitLocale] && option.langs[parentInitLocale]) {
                option.langs[childInitLocale] = { ...option.langs[parentInitLocale] }
            }
        })
    }

    // insert inheritedOptions into child
    if (childQuestion.optionSetY) {
        childQuestion.optionSetY.options = [
            ...inheritedOptions,
            ...childQuestion.optionSetY.options,
        ]
    } else if (childQuestion.optionSet) {
        childQuestion.optionSet.options = [
            ...inheritedOptions,
            ...childQuestion.optionSet.options,
        ]
    }
}

export {
    getTitle,
    getQuestionTitle,
    getOptionMetric,
    getOptionTitle,
    isNPSQuestion,
    kpiTypeFor,
    isSingleAxis,
    isDoubleAxis,
    getParentQuestion,
    getParentOptions,
    getParentFromQlist,
    getQuestionById,
    insertInheritedOptions,
}
